import {
    ESalesAssistClientStatusFilter,
    ESalesAssistQualification,
    ESalesAssistStatusBadge,
} from '../models/sales-assist.model'

export const SALES_ASSIST_ROUTE_PATHS = {
    BASE: 'sales-assist',
} as const

export const SALES_ASSIST_DEFAULTS = {
    PAGE_SIZE: 20,
    SEARCH_MAX_PAGES: 3,
    DEEP_CITY_SEARCH_ENABLED: true,
    NEARBY_RADIUS_METERS: 3000,
    /** Nominatim public usage is about one request per second. */
    PLACE_SEARCH_MIN_INTERVAL_MS: 1000,
    MAP_CENTER_LATITUDE: 22.5937,
    MAP_CENTER_LONGITUDE: 78.9629,
    MAP_CENTER_ZOOM: 5,
    MAP_PIN_ZOOM: 13,
    GEOLOCATION_MAXIMUM_AGE_MS: 5 * 60 * 1000,
    GEOLOCATION_TIMEOUT_MS: 10000,
    QUALIFIED_SCORE_THRESHOLD: 80,
    EXISTING_MATCH_THRESHOLD: 90,
    POSSIBLE_MATCH_THRESHOLD: 75,
    DISCOVERY_SCORE_FALLBACK: 65,
    REVIEW_SCORE_DIVISOR: 20,
    REVIEW_SCORE_MAX_BOOST: 10,
    DISCOVERY_SCORE_MIN: 50,
    DISCOVERY_SCORE_MAX: 99,
    PAGE_SIZE_OPTIONS: [10, 20, 25, 50],
} as const

export const SALES_ASSIST_PROPOSAL_FIELDS = 'basic,configuration,products,pricing' as const

export const SALES_ASSIST_PAGE_COPY = {
    TITLE: 'Sales Assist',
    VIEW_LABEL: 'Discovery',
    FIND_SCHOOLS: 'Find schools',
    CITY_MODE: 'City',
    NEAR_ME_MODE: 'Near me',
    CHOOSE_LOCATION_MODE: 'Choose location',
    NEAR_ME_TITLE: 'Nearby discovery',
    CHOOSE_LOCATION_TITLE: 'Choose a base location',
    NEAR_ME_DESCRIPTION:
        'Pull schools within a {radiusKm} km straight-line radius of your location (not driving distance).',
    CHOOSE_LOCATION_DESCRIPTION:
        'Pin a place, then pull schools within a {radiusKm} km straight-line radius (not driving distance).',
    PIN_REQUIRED: 'Pin a location on the map before pulling schools.',
    PIN_COORDINATES_LABEL: 'Selected location',
    RESULTS_FOR_PREVIOUS_PIN:
        'Results are for the last searched pin. Pull schools again to search from the new pin.',
    PLAN_VISIT: 'Plan visit',
    MAP_ARIA_LABEL: 'Map to choose a base location',
    MAP_SEARCH_LABEL: 'Search place',
    MAP_SEARCH_PLACEHOLDER: 'City, state, or country',
    MAP_SEARCH_ACTION: 'Search',
    MAP_SEARCH_CLEAR: 'Clear',
    MAP_SEARCH_EMPTY: 'No matching place.',
    MAP_SEARCH_FAILED: 'Place search failed. Try again.',
    MAP_SEARCH_BUSY: 'Searching…',
    MAP_LOAD_FAILED: 'The map could not be loaded. Refresh the page and try again.',
    MAP_SEARCH_RESULTS_LABEL: 'Matching places',
    MAP_TILE_URL: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    MAP_TILE_ATTRIBUTION: '&copy; OpenStreetMap contributors',
    CITY_LABEL: 'City',
    CITY_PLACEHOLDER: 'e.g. Ahmedabad',
    BOARD_LABEL: 'Board filter',
    CLIENT_STATUS_FILTER_LABEL: 'Client status filter',
    PULL_SCHOOLS: 'Pull schools',
    REFRESH_SCHOOLS: 'Refresh',
    REFRESHING_SCHOOLS: 'Refreshing…',
    SCHOOLS_COUNT_SUFFIX: 'schools',
    DISCOVERING: 'Discovering schools…',
    DISCOVERING_HINT: 'Scanning maps and matching schools in your area',
    ALERT_MESSAGE: 'Use Pull schools for saved results, or Refresh for the latest schools.',
    // ALERT_MESSAGE:
    //     'Enter a city or choose Near me, then click Pull schools to fetch Google Maps discovery results.',
    ALERT_APPLY: 'Start',
    DETAILS: 'Details',
    ENRICH: 'Enrich',
    ENRICHING: 'Enriching…',
    ENRICHING_TOOLTIP: 'Enriching with AI — this may take up to a minute',
    ENRICHING_BLOCKED_TOOLTIP: 'Please wait — one enrichment at a time',
    VIEW_ENRICHED: 'View enriched',
    ADD_CLIENT: 'Add client',
    ADDING_CLIENT: 'Adding…',
    LOCATING: 'Waiting for location permission…',
    SEARCHING_NEARBY: 'Searching nearby schools…',
    NO_SCHOOLS_IN_AREA: 'No schools found in this area.',
    CLIENT_STATUS_FILTER_EMPTY:
        'No schools match this client status filter. Try another filter or choose All Clients.',
    LOCATION_DENIED: 'Enable location in browser settings and try again.',
    LOCATION_UNAVAILABLE: 'Unable to detect your current location. Please try again.',
    LOCATION_TIMEOUT: 'Location detection timed out. Please try again.',
    LOCATION_NOT_SUPPORTED: 'Location access is not available in this browser.',
    SEARCH_TIMEOUT: 'Search took too long. Try again.',
    LOADED_FROM_CACHE: 'Loaded from saved results',
    LOADED_FROM_GOOGLE_MAPS: 'Loaded from Google Maps',
    NEARBY_RESULTS_FROM_CACHE_STATUS:
        'Found {schoolLabel} within {radiusKm} km from your saved results.',
    NEARBY_RESULTS_FROM_CACHE_TOAST: 'Loaded {schoolLabel} from your recent search.',
    NEARBY_RESULTS_FROM_MAPS_STATUS: 'Found {schoolLabel} within {radiusKm} km using Google Maps.',
    NEARBY_RESULTS_FROM_MAPS_TOAST: 'Discovered {schoolLabel} near your location.',
    NEARBY_REFRESH_BOARD_PROGRESS: 'Loading {board}… ({current}/{total})',
    NEARBY_RESULTS_EMPTY_STATUS: 'No schools found within {radiusKm} km of your location.',
    NEARBY_RESULTS_EMPTY_TOAST:
        'No schools found nearby. Try changing the board filter or searching from a different location.',
    READY_TO_DISCOVER: 'Ready to discover',
    READY_TO_DISCOVER_HINT: 'Click Pull schools to discover nearby schools using your location.',
    CRM_PAST_CLIENT: 'Past client',
    CRM_LAST_PURCHASE: 'Last purchase',
    CRM_CLIENT_INSIGHTS: 'Client insights',
    CRM_TOTAL_VALUE: 'Total value',
    CRM_STUDENTS: 'Students',
    CRM_PRODUCTS: 'Products',
    ORDER_HISTORY_TAB: 'Order history',
    ORDER_HISTORY_YEAR: 'Year',
    ORDER_HISTORY_LOADING: 'Loading order history…',
    ORDER_HISTORY_EMPTY: 'No order history found for this school.',
    ORDER_HISTORY_LOAD_ERROR: 'Unable to load order history. Please try again.',
    ORDER_HISTORY_UNAVAILABLE: 'Order history is available only for matched EI schools.',
    AI_DISCLAIMER_BANNER: 'AI can make mistakes. Kindly double-check the information.',
} as const

export const SALES_ASSIST_REGEX_PATTERNS = {
    /** City label accepted by client create API: letters, digits, and spaces only. */
    API_CITY_LABEL: /^[A-Za-z0-9 ]+$/,
    /** "Near lat, lng" style discovery labels. */
    COORDINATE_NEAR_PREFIX: /^near\s+/i,
    /** Signed decimal number (e.g. latitude fragment in a coordinate label). */
    SIGNED_DECIMAL_NUMBER: /-?\d+(\.\d+)?/,
    /** Lat,lng coordinate pair label. */
    LAT_LNG_COORDINATE_PAIR: /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/,
} as const

export const SALES_ASSIST_CARD_ACTIONS = {
    enrich: {
        icon: 'wand',
        enrichedIcon: 'sparkles',
        label: 'Enrich',
        enrichingLabel: 'Enriching…',
        enrichingTooltip: 'Enriching with AI — this may take up to a minute',
        enrichingBlockedTooltip: 'Please wait — one enrichment at a time',
        enrichedLabel: 'View enriched',
    },
    addClient: {
        icon: 'user_plus',
        label: 'Add client',
        loadingLabel: 'Adding…',
    },
    details: {
        icon: 'eye',
        label: 'Details',
    },
} as const

export const SALES_ASSIST_BOARD_OPTIONS = [
    { label: 'All Boards', value: ESalesAssistQualification.All },
    { label: 'CBSE', value: 'cbse' },
    { label: 'ICSE', value: 'icse' },
    { label: 'Cambridge', value: 'cambridge' },
    { label: 'IB', value: 'ib' },
    { label: 'State Board', value: 'state' },
] as const

/** Board values used for multi-query discovery when "All Boards" is selected. */
export const SALES_ASSIST_DISCOVERY_BOARD_VALUES = SALES_ASSIST_BOARD_OPTIONS.filter(
    (option) => option.value !== ESalesAssistQualification.All,
).map((option) => option.value)

/** Near-me refresh runs boards in this order (dense boards first). */
export const SALES_ASSIST_NEARBY_BOARD_PRIORITY_ORDER = [
    'cbse',
    'icse',
    'ib',
    'state',
    'cambridge',
] as const

export const SALES_ASSIST_CLIENT_STATUS_FILTER_OPTIONS = [
    { label: 'All Clients', value: ESalesAssistClientStatusFilter.All },
    { label: 'EI record', value: ESalesAssistClientStatusFilter.EiRecord },
    { label: 'New school', value: ESalesAssistClientStatusFilter.New },
    { label: 'Existing school', value: ESalesAssistClientStatusFilter.Existing },
    { label: 'Past client', value: ESalesAssistClientStatusFilter.Past },
] as const

export const SALES_ASSIST_QUALIFICATION_OPTIONS = [
    { id: ESalesAssistQualification.All, label: 'All' },
    { id: ESalesAssistQualification.Target, label: 'Target' },
    { id: ESalesAssistQualification.Qualified, label: 'Qualified' },
    { id: ESalesAssistQualification.Watching, label: 'Watching' },
]

export const SALES_ASSIST_STATUS_BADGE_LABELS: Record<ESalesAssistStatusBadge, string> = {
    [ESalesAssistStatusBadge.EiClient]: 'EI Client',
    [ESalesAssistStatusBadge.Target]: 'Target',
    [ESalesAssistStatusBadge.New]: 'New school',
    [ESalesAssistStatusBadge.Existing]: 'Existing school',
    [ESalesAssistStatusBadge.PastClient]: 'Past client',
    [ESalesAssistStatusBadge.EiRecord]: 'EI record',
    [ESalesAssistStatusBadge.PossibleMatch]: 'Possible match',
    [ESalesAssistStatusBadge.Qualified]: 'Qualified',
    [ESalesAssistStatusBadge.Watching]: 'Watching',
    [ESalesAssistStatusBadge.AiEnriched]: 'AI Enriched',
}
